/**
 * Mock data for the admin dashboard.
 *
 * Ported from the design handoff's prototype logic block. Shapes are kept
 * realistic so the UI is visually complete and reviewable before any API exists
 * (STEP 5 of the brief). Nothing here talks to a backend.
 *
 * The handoff arrived mis-encoded (UTF-8 read as Latin-1), so every "Â£" is a
 * "£", every "Â·" a "·", and the various "â..." sequences are em/en dashes,
 * ticks and triangles. Repaired at the source here rather than in the views.
 */

/* ---------------------------------------------------------------- users --- */

export const PERSONA_TAG_CLASS = {
  individual: "tag-neutral",
  sole_trader: "tag-accent-2",
  company: "tag-accent",
};

export const PERSONA_TYPE_LABEL = {
  individual: "Individual",
  sole_trader: "Sole trader",
  company: "Company",
};

export const USER_STATUS_META = {
  active: { label: "Active", tagClass: "tag-accent-2" },
  pending: { label: "Pending", tagClass: "tag-outline" },
  suspended: { label: "Suspended", tagClass: "tag-neutral" },
};

export const USERS = [
  {
    id: "u1", name: "Daniel Osei", email: "daniel.osei@gmail.com",
    location: "Peckham, London · SE15", signup: "12 Nov 2024", status: "active",
    personas: [{ type: "individual", label: "Homeowner", tier: 2, missing: ["Proof of address (utility bill, <3mo)"] }],
  },
  {
    id: "u2", name: "Sarah Whitfield", email: "s.whitfield@outlook.com",
    location: "Chorlton, Manchester · M21", signup: "3 Feb 2024", status: "active",
    personas: [{ type: "sole_trader", label: "Electrician", tier: 3, missing: [] }],
  },
  {
    id: "u3", name: "Grady Construction Ltd", email: "info@gradyconstruction.co.uk",
    location: "Edgbaston, Birmingham · B15", signup: "18 Jan 2023", status: "active",
    personas: [{ type: "company", label: "General Contractor", tier: 2, missing: ["Companies House confirmation statement (2026)"] }],
  },
  {
    id: "u4", name: "Priya Nair", email: "p.nair@gmail.com",
    location: "Clifton, Bristol · BS8", signup: "22 Jun 2024", status: "active",
    personas: [
      { type: "individual", label: "Homeowner", tier: 1, missing: ["Proof of address"] },
      { type: "sole_trader", label: "Plumber", tier: 2, missing: ["Public liability insurance renewal"] },
    ],
  },
  {
    id: "u5", name: "Tomasz Nowak", email: "t.nowak@gmail.com",
    location: "Chapel Allerton, Leeds · LS7", signup: "5 Sep 2025", status: "active",
    personas: [{ type: "sole_trader", label: "Roofer", tier: 1, missing: ["DBS check", "Trade insurance certificate"] }],
  },
  {
    id: "u6", name: "Heron & Vale Builders Ltd", email: "ops@heronvale.co.uk",
    location: "Clerkenwell, London · EC1R", signup: "9 Apr 2022", status: "active",
    personas: [{ type: "company", label: "General Contractor", tier: 3, missing: [] }],
  },
  {
    id: "u7", name: "Fiona Marsh", email: "fiona.marsh@yahoo.co.uk",
    location: "Hyndland, Glasgow · G12", signup: "30 Jul 2025", status: "pending",
    personas: [{ type: "individual", label: "Homeowner", tier: 0, missing: ["Government ID", "Proof of address"] }],
  },
  {
    id: "u8", name: "Connor Boyle", email: "c.boyle@gmail.com",
    location: "Malone, Belfast · BT9", signup: "14 May 2025", status: "suspended",
    personas: [{ type: "sole_trader", label: "Carpenter", tier: 1, missing: ["Trade insurance certificate", "2 client references"] }],
  },
  {
    id: "u9", name: "Ashworth Group Ltd", email: "accounts@ashworthgroup.co.uk",
    location: "Holbeck, Leeds · LS11", signup: "2 Dec 2025", status: "active",
    personas: [{ type: "company", label: "Developer", tier: 1, missing: ["Companies House verification", "Director ID checks (2 pending)"] }],
  },
  {
    id: "u10", name: "Bethan Rees", email: "b.rees@gmail.com",
    location: "Uplands, Swansea · SA2", signup: "11 Oct 2023", status: "active",
    personas: [
      { type: "sole_trader", label: "Bathroom Fitter", tier: 3, missing: [] },
      { type: "company", label: "Rees Interiors Ltd (Director)", tier: 2, missing: ["Companies House confirmation statement"] },
    ],
  },
  {
    id: "u11", name: "Kaur Interiors Ltd", email: "finance@kaurinteriors.co.uk",
    location: "Shoreditch, London · N1", signup: "6 Mar 2021", status: "active",
    personas: [{ type: "company", label: "Interior Contractor", tier: 3, missing: [] }],
  },
  {
    id: "u12", name: "Michael O'Brien", email: "m.obrien@gmail.com",
    location: "Pontcanna, Cardiff · CF11", signup: "8 Aug 2024", status: "active",
    personas: [{ type: "individual", label: "Homeowner", tier: 2, missing: ["Reference check"] }],
  },
];

export const PERSONA_FILTERS = [
  { value: "all", label: "All" },
  { value: "individual", label: "Individual" },
  { value: "sole_trader", label: "Sole trader" },
  { value: "company", label: "Company" },
];

export const TIER_FILTERS = [
  { value: "all", label: "All tiers" },
  { value: "0", label: "Tier 0 — Unverified" },
  { value: "1", label: "Tier 1" },
  { value: "2", label: "Tier 2" },
  { value: "3", label: "Tier 3 — Full" },
];

/* ------------------------------------------------------------- payments --- */

export const PAYMENT_STATUS_META = {
  succeeded: { color: "var(--status-ok)", label: "Succeeded" },
  failed: { color: "var(--status-danger)", label: "Failed" },
  disputed: { color: "var(--status-danger)", label: "Disputed" },
  refunded: { color: "var(--status-muted)", label: "Refunded" },
  pending: { color: "var(--status-warn)", label: "Pending" },
};

export const PAYMENTS = [
  { id: "TXN-88213", user: "Daniel Osei", amount: "£1,240.00", description: "Kitchen refit deposit", status: "succeeded", date: "28 Jul 2026", card: "Visa ••4242" },
  { id: "TXN-88214", user: "Sarah Whitfield", amount: "£49.00", description: "Pro subscription — monthly", status: "succeeded", date: "27 Jul 2026", card: "Mastercard ••0051" },
  { id: "TXN-88220", user: "Kaur Interiors Ltd", amount: "£399.00", description: "Scale plan — monthly", status: "succeeded", date: "26 Jul 2026", card: "Amex ••1009" },
  { id: "TXN-88231", user: "Grady Construction Ltd", amount: "£129.00", description: "Growth plan — monthly", status: "failed", date: "25 Jul 2026", card: "Visa ••7731", reason: "Card declined — insufficient funds" },
  { id: "TXN-88240", user: "Connor Boyle", amount: "£850.00", description: "Milestone payment — Kitchen refit", status: "disputed", date: "24 Jul 2026", card: "Mastercard ••4423", reason: "Customer claims work incomplete" },
  { id: "TXN-88245", user: "Priya Nair", amount: "£3,200.00", description: "Bathroom renovation — first fix", status: "succeeded", date: "23 Jul 2026", card: "Visa ••9081" },
  { id: "TXN-88250", user: "Ashworth Group Ltd", amount: "£0.00", description: "Trial started — Growth plan", status: "pending", date: "22 Jul 2026", card: "—" },
  { id: "TXN-88190", user: "Fiona Marsh", amount: "£1,850.00", description: "Roofing repair deposit", status: "refunded", date: "15 Jul 2026", card: "Visa ••3312" },
  { id: "TXN-88255", user: "Heron & Vale Builders Ltd", amount: "£999.00", description: "Scale plan — annual", status: "succeeded", date: "20 Jul 2026", card: "Amex ••2201" },
  { id: "TXN-88260", user: "Bethan Rees", amount: "£64.00", description: "Pro subscription — monthly", status: "failed", date: "19 Jul 2026", card: "Mastercard ••6650", reason: "Card expired" },
];

export const ENTITLEMENTS = [
  { name: "Grady Construction Ltd", plan: "Growth", seatsUsed: 5, seatsTotal: 8, renewalLabel: "Renews 25 Aug 2026", atRisk: true, trial: false },
  { name: "Kaur Interiors Ltd", plan: "Scale", seatsUsed: 18, seatsTotal: 25, renewalLabel: "Renews 26 Aug 2026", atRisk: false, trial: false },
  { name: "Heron & Vale Builders Ltd", plan: "Scale (annual)", seatsUsed: 30, seatsTotal: 40, renewalLabel: "Renews 20 Jul 2027", atRisk: false, trial: false },
  { name: "Ashworth Group Ltd", plan: "Growth", seatsUsed: 2, seatsTotal: 8, renewalLabel: "Trial ends 5 Aug 2026", atRisk: false, trial: true },
];

/* ------------------------------------------------------ service requests --- */

export const STAGE_META = {
  draft: { color: "var(--status-muted)", label: "Draft" },
  posted: { color: "var(--status-info)", label: "Posted" },
  bidding: { color: "var(--status-warn)", label: "Bidding" },
  awarded: { color: "var(--status-ok)", label: "Awarded" },
  in_progress: { color: "var(--status-ok)", label: "In progress" },
  complete: { color: "var(--status-idle)", label: "Complete" },
};

export const STAGE_FILTERS = [
  { value: "all", label: "All stages" },
  { value: "draft", label: "Draft" },
  { value: "posted", label: "Posted" },
  { value: "bidding", label: "Bidding" },
  { value: "awarded", label: "Awarded" },
  { value: "in_progress", label: "In progress" },
  { value: "complete", label: "Complete" },
];

export const MILESTONE_STATE_META = {
  paid: { color: "var(--status-ok)", mark: "✓", label: "Paid" },
  due: { color: "var(--status-muted)", mark: "·", label: "Due" },
  locked: { color: "var(--status-warn)", mark: "⚠", label: "Locked" },
};

const ms = (name, pct, state, lockReason) => ({ name, pct, state, lockReason });

export const REQUESTS = [
  {
    id: "SR-4021", title: "Full kitchen refit", category: "Kitchen Refit", homeowner: "Daniel Osei",
    budget: "£15,000–£20,000", timeline: "6–8 weeks", stage: "in_progress",
    location: "Peckham, London", provenance: "kitchen_full_refit v3",
    milestones: [ms("Deposit", 10, "paid"), ms("First fix", 30, "paid"), ms("Second fix", 30, "locked", "Awaiting building control sign-off"), ms("Snagging & completion", 30, "due")],
    media: [{ isAi: false, label: "Existing kitchen" }, { isAi: false, label: "Existing kitchen — rear wall" }, { isAi: true, label: "Concept — island layout" }, { isAi: true, label: "Concept — finish palette" }],
  },
  {
    id: "SR-4030", title: "Loft conversion with en-suite", category: "Loft Conversion", homeowner: "Priya Nair",
    budget: "£35,000–£45,000", timeline: "10–12 weeks", stage: "bidding",
    location: "Clifton, Bristol", provenance: "loft_conversion_ensuite v2",
    milestones: [ms("Deposit", 10, "paid"), ms("Structural", 35, "due"), ms("First fix", 25, "due"), ms("Completion", 30, "due")],
    media: [{ isAi: false, label: "Loft space — current" }, { isAi: true, label: "Concept — en-suite layout" }],
  },
  {
    id: "SR-4008", title: "Roof repair — storm damage", category: "Roofing", homeowner: "Fiona Marsh",
    budget: "£1,500–£2,500", timeline: "1–2 weeks", stage: "complete",
    location: "Hyndland, Glasgow", provenance: "roofing_storm_repair v1",
    milestones: [ms("Deposit", 20, "paid"), ms("Repair works", 60, "paid"), ms("Completion", 20, "paid")],
    media: [{ isAi: false, label: "Storm damage — before" }, { isAi: false, label: "Completed repair" }],
  },
  {
    id: "SR-4045", title: "Rear extension — single storey", category: "Extension", homeowner: "Michael O'Brien",
    budget: "£60,000–£80,000", timeline: "14–16 weeks", stage: "awarded",
    location: "Pontcanna, Cardiff", provenance: "extension_single_storey v4",
    milestones: [ms("Deposit", 10, "paid"), ms("Foundations", 25, "locked", "Party wall agreement pending"), ms("Structure", 35, "due"), ms("Finishing", 30, "due")],
    media: [{ isAi: false, label: "Rear garden — current" }, { isAi: true, label: "Concept — extension exterior" }, { isAi: true, label: "Concept — kitchen-diner interior" }],
  },
  {
    id: "SR-4051", title: "Bathroom renovation", category: "Bathroom Renovation", homeowner: "Priya Nair",
    budget: "£8,000–£12,000", timeline: "3–4 weeks", stage: "in_progress",
    location: "Clifton, Bristol", provenance: "bathroom_renovation v2",
    milestones: [ms("Deposit", 15, "paid"), ms("First fix", 35, "paid"), ms("Second fix", 35, "locked", "Waterproofing inspection outstanding"), ms("Completion", 15, "due")],
    media: [{ isAi: false, label: "Existing bathroom" }, { isAi: true, label: "Concept — tiling scheme" }],
  },
  {
    id: "SR-4060", title: "Full rewire — 3 bed semi", category: "Electrical", homeowner: "Michael O'Brien",
    budget: "£4,500–£6,000", timeline: "1 week", stage: "posted",
    location: "Pontcanna, Cardiff", provenance: "rewire_full_house v1",
    milestones: [ms("Deposit", 20, "due"), ms("Rewire works", 60, "due"), ms("Certification", 20, "due")],
    media: [{ isAi: false, label: "Consumer unit — current" }],
  },
  {
    id: "SR-4065", title: "Garden landscaping & patio", category: "Landscaping", homeowner: "Daniel Osei",
    budget: "£6,000–£9,000", timeline: "2–3 weeks", stage: "draft",
    location: "Peckham, London", provenance: "landscaping_patio v1",
    milestones: [ms("Deposit", 20, "due"), ms("Groundworks", 40, "due"), ms("Planting & finish", 40, "due")],
    media: [{ isAi: false, label: "Garden — current" }],
  },
  {
    id: "SR-4070", title: "Exterior painting & decorating", category: "Painting & Decorating", homeowner: "Fiona Marsh",
    budget: "£1,200–£1,800", timeline: "3–5 days", stage: "draft",
    location: "Hyndland, Glasgow", provenance: "painting_exterior v1",
    milestones: [ms("Deposit", 30, "due"), ms("Completion", 70, "due")],
    media: [{ isAi: false, label: "Exterior — current" }],
  },
];

/* -------------------------------------------------------------- tickets --- */

export const SEVERITY_META = {
  critical: { color: "var(--status-danger)", label: "Critical" },
  high: { color: "var(--status-warn)", label: "High" },
  medium: { color: "var(--status-info)", label: "Medium" },
  low: { color: "var(--status-idle)", label: "Low" },
};

export const SEVERITY_FILTERS = [
  { value: "all", label: "All" },
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

export const TICKET_STATUS_META = {
  open: { tagClass: "tag-outline", label: "Open" },
  investigating: { tagClass: "tag-accent-2", label: "Investigating" },
  resolved: { tagClass: "tag-neutral", label: "Resolved" },
};

export const TICKET_STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  { value: "open", label: "Open" },
  { value: "investigating", label: "Investigating" },
  { value: "resolved", label: "Resolved" },
];

const stage = (n, text, extra) => ({ stage: n, text, ...(extra || {}) });

export const TICKETS = [
  {
    id: "TCK-1042", subject: "Still on trial after payment", requester: "Kaur Interiors Ltd",
    severity: "critical", category: "billing", status: "investigating", created: "2 hours ago",
    snippet: "Paid for the Scale plan but the dashboard still shows the Trial banner and a seat limit of 5.",
    trace: [
      stage(1, 'Classified as "billing" issue', { meta: "confidence 92%" }),
      stage(2, "Automated checks run", { checks: [
        { label: "Stripe payment check", result: "Payment of £399.00 found — succeeded 26 Jul 2026", ok: true },
        { label: "Entitlement check", result: "Account still shows Trial / Growth-trial tier", ok: false },
      ] }),
      stage(3, "Hypothesis: entitlement did not update after payment — webhook likely missed or failed silently"),
      stage(4, "Suggested fix: manually sync entitlement to Scale plan and clear the trial flag", { isFix: true }),
    ],
  },
  {
    id: "TCK-1039", subject: "Tradesperson didn't get payout", requester: "Sarah Whitfield",
    severity: "high", category: "payments", status: "open", created: "5 hours ago",
    snippet: "Milestone marked paid on the project but the payout hasn't landed in my account.",
    trace: [
      stage(1, 'Classified as "payout" issue', { meta: "confidence 87%" }),
      stage(2, "Automated checks run", { checks: [
        { label: "Stripe transfer check", result: "Transfer scheduled for 29 Jul 2026 (T+3 standard)", ok: true },
        { label: "Bank account check", result: "Connected account verified", ok: true },
      ] }),
      stage(3, "Hypothesis: payout is within the normal T+3 processing window — not an error"),
      stage(4, "Suggested fix: no action needed — notify user of expected arrival date", { isFix: true }),
    ],
  },
  {
    id: "TCK-1035", subject: "Can't upload insurance certificate", requester: "Tomasz Nowak",
    severity: "medium", category: "verification", status: "open", created: "1 day ago",
    snippet: "File upload keeps failing on the documents tab.",
    trace: [
      stage(1, 'Classified as "verification / upload" issue', { meta: "confidence 78%" }),
      stage(2, "Automated checks run", { checks: [
        { label: "File size check", result: "Upload exceeds 10MB limit (14.2MB)", ok: false },
      ] }),
      stage(3, "Hypothesis: file exceeds the platform upload limit"),
      stage(4, "Suggested fix: ask user to compress the file, or raise limit for verified device types", { isFix: true }),
    ],
  },
  {
    id: "TCK-1028", subject: "Disputed milestone payment", requester: "Connor Boyle",
    severity: "critical", category: "disputes", status: "investigating", created: "2 days ago",
    snippet: "Homeowner disputed the second-fix milestone payment, claims work incomplete.",
    trace: [
      stage(1, 'Classified as "payment dispute"', { meta: "confidence 95%" }),
      stage(2, "Automated checks run", { checks: [
        { label: "Milestone sign-off check", result: "No homeowner sign-off recorded before release", ok: false },
        { label: "Media evidence check", result: "3 progress photos on file from tradesperson", ok: true },
      ] }),
      stage(3, "Hypothesis: milestone auto-released after the due-diligence window expired without explicit sign-off"),
      stage(4, "Suggested fix: escalate to manual dispute review, hold remaining balance", { isFix: true }),
    ],
  },
  {
    id: "TCK-1019", subject: "Duplicate account created", requester: "Michael O'Brien",
    severity: "low", category: "account", status: "resolved", created: "4 days ago",
    snippet: "Accidentally signed up twice with two emails.",
    trace: [
      stage(1, 'Classified as "duplicate account"', { meta: "confidence 99%" }),
      stage(2, "Automated checks run", { checks: [
        { label: "Identity match check", result: "Same name + phone number on both accounts", ok: true },
      ] }),
      stage(3, "Hypothesis: accidental duplicate signup, no fraud signal"),
      stage(4, "Fix applied: accounts merged, secondary email retained as alias", { isFix: true }),
    ],
  },
  {
    id: "TCK-1044", subject: "Seat count looks wrong", requester: "Ashworth Group Ltd",
    severity: "medium", category: "billing", status: "open", created: "1 hour ago",
    snippet: "We only have 2 active users but it's charging for 8 seats.",
    trace: [
      stage(1, 'Classified as "entitlement / seats" issue', { meta: "confidence 81%" }),
      stage(2, "Automated checks run", { checks: [
        { label: "Seat usage check", result: "2 active, 8 licensed on Growth trial", ok: true },
        { label: "Billing check", result: "Trial — no charge yet, converts 5 Aug 2026", ok: true },
      ] }),
      stage(3, "Hypothesis: no billing error — trial seat allocation defaults to 8, not usage-based"),
      stage(4, "Suggested fix: no action — clarify trial seat allocation to customer", { isFix: true }),
    ],
  },
  {
    id: "TCK-1011", subject: "Can't switch between homeowner and trade profile", requester: "Priya Nair",
    severity: "high", category: "technical", status: "investigating", created: "6 hours ago",
    snippet: "The persona switcher isn't showing my Plumber profile anymore.",
    trace: [
      stage(1, 'Classified as "persona / technical" issue', { meta: "confidence 84%" }),
      stage(2, "Automated checks run", { checks: [
        { label: "Persona record check", result: "Sole trader persona exists, tier 2", ok: true },
        { label: "Session cache check", result: "Stale persona list cached client-side", ok: false },
      ] }),
      stage(3, "Hypothesis: client-side persona cache went stale after a tier upgrade"),
      stage(4, "Suggested fix: force a session refresh for the affected account", { isFix: true }),
    ],
  },
  {
    id: "TCK-1002", subject: "Refund not received", requester: "Fiona Marsh",
    severity: "medium", category: "payments", status: "resolved", created: "1 week ago",
    snippet: "Cancelled the roofing job, refund still hasn't arrived.",
    trace: [
      stage(1, 'Classified as "refund" issue', { meta: "confidence 90%" }),
      stage(2, "Automated checks run", { checks: [
        { label: "Stripe refund check", result: "Refund of £1,850.00 processed 15 Jul 2026", ok: true },
      ] }),
      stage(3, "Hypothesis: refund processed, likely a bank posting delay (5-10 business days)"),
      stage(4, "Fix applied: confirmed with customer, refund landed", { isFix: true }),
    ],
  },
];

/* ------------------------------------------------------------ ai copilot --- */

export const AI_SUGGESTIONS = [
  { id: "a1", area: "Entitlements", title: "Entitlement mismatch detected", detail: "Kaur Interiors Ltd shows Trial but the last Stripe payment succeeded 2 days ago. Suggest syncing entitlement to Scale." },
  { id: "a2", area: "Payments", title: "Possible duplicate charge", detail: "Two £850.00 charges to Connor Boyle 4 minutes apart. Suggest flagging the second for refund review." },
  { id: "a3", area: "Verification", title: "Verification stall", detail: "6 sole-trader accounts have been stuck at Tier 1 for 20+ days pending insurance document review. Suggest a reminder batch." },
];

/* ------------------------------------------------------- admin access ----- */
/* Not present in the handoff design — modelled to match the system's language.
   See NOTES.md for the design decisions behind this section.               */

export const STAFF_ROLES = [
  { value: "owner", label: "Owner", blurb: "Full access, including billing and staff management." },
  { value: "admin", label: "Admin", blurb: "Everything except billing and removing owners." },
  { value: "support", label: "Support", blurb: "Tickets, users and service requests. No financial data." },
  { value: "finance", label: "Finance", blurb: "Payments, entitlements and disputes only." },
  { value: "readonly", label: "Read-only", blurb: "Can view every section but cannot act on anything." },
];

export const STAFF_ROLE_LABEL = STAFF_ROLES.reduce((acc, r) => {
  acc[r.value] = r.label;
  return acc;
}, {});

export const STAFF_STATUS_META = {
  active: { label: "Active", tagClass: "tag-accent-2" },
  invited: { label: "Invited", tagClass: "tag-outline" },
  suspended: { label: "Suspended", tagClass: "tag-neutral" },
};

export const STAFF = [
  { id: "s1", name: "Olivia Ashcroft", email: "olivia.ashcroft@buildbeta.co.uk", role: "owner", status: "active", mfa: true, lastActive: "12 minutes ago", initials: "OA" },
  { id: "s2", name: "Rahul Mehta", email: "rahul.mehta@buildbeta.co.uk", role: "admin", status: "active", mfa: true, lastActive: "1 hour ago", initials: "RM" },
  { id: "s3", name: "Jade Fowler", email: "jade.fowler@buildbeta.co.uk", role: "support", status: "active", mfa: true, lastActive: "3 hours ago", initials: "JF" },
  { id: "s4", name: "Peter Lindqvist", email: "peter.lindqvist@buildbeta.co.uk", role: "finance", status: "active", mfa: false, lastActive: "Yesterday", initials: "PL" },
  { id: "s5", name: "Nadia Hussain", email: "nadia.hussain@buildbeta.co.uk", role: "support", status: "invited", mfa: false, lastActive: "Never — invited 2 days ago", initials: "NH" },
  { id: "s6", name: "Tom Beckett", email: "tom.beckett@buildbeta.co.uk", role: "readonly", status: "active", mfa: true, lastActive: "4 days ago", initials: "TB" },
  { id: "s7", name: "Claire Donnelly", email: "claire.donnelly@buildbeta.co.uk", role: "admin", status: "suspended", mfa: true, lastActive: "3 weeks ago", initials: "CD" },
];

/* Which nav sections each role can reach. Drives the role-scoped welcome on
   the last onboarding step. */
export const ROLE_SCOPE = {
  owner: ["Dashboard", "Users", "Payments", "Service Requests", "Tickets", "Taxonomy", "Admin Access"],
  admin: ["Dashboard", "Users", "Service Requests", "Tickets", "Taxonomy", "Admin Access"],
  support: ["Dashboard", "Users", "Service Requests", "Tickets"],
  finance: ["Dashboard", "Payments"],
  readonly: ["Dashboard", "Users", "Payments", "Service Requests", "Tickets", "Taxonomy"],
};

/* The invited staffer the onboarding flow runs as. */
export const ONBOARDING_INVITEE = {
  name: "Nadia Hussain",
  email: "nadia.hussain@buildbeta.co.uk",
  role: "support",
  invitedBy: "Olivia Ashcroft",
};

/* ------------------------------------------------------------- taxonomy --- */

const tx = (name, tasks) => ({ name, tasks });

export const TAXONOMY = [
  {
    id: "c1", name: "Kitchen Refit",
    description: "Full or partial kitchen replacement, including units, worktops and first/second-fix trades.",
    trades: ["Kitchen Fitter", "Plumber", "Electrician", "Plasterer"],
    projectType: "kitchen_full_refit v3", timeline: "6–8 weeks", budget: "£12,000 – £25,000",
    phases: [
      tx("Prep", ["Site survey & measure", "Strip out existing kitchen", "First-fix electrics & plumbing"]),
      tx("Construction", ["Install units & worktops", "Second-fix electrics & plumbing", "Tiling"]),
      tx("Finishing", ["Appliance install & test", "Snagging walkthrough", "Final clean"]),
    ],
  },
  {
    id: "c2", name: "Bathroom Renovation",
    description: "Strip-out and refit of a bathroom or en-suite, including tiling and waterproofing.",
    trades: ["Bathroom Fitter", "Plumber", "Electrician", "Tiler"],
    projectType: "bathroom_renovation v2", timeline: "3–4 weeks", budget: "£6,000 – £14,000",
    phases: [
      tx("Prep", ["Strip out existing suite", "First-fix plumbing & electrics"]),
      tx("Construction", ["Waterproofing & tiling", "Fit suite & shower enclosure"]),
      tx("Finishing", ["Sealant & snagging", "Final clean"]),
    ],
  },
  {
    id: "c3", name: "Loft Conversion",
    description: "Habitable loft conversion with staircase, insulation and structural works.",
    trades: ["Carpenter", "Roofer", "Electrician", "Plasterer", "Structural Engineer"],
    projectType: "loft_conversion_ensuite v2", timeline: "10–12 weeks", budget: "£30,000 – £55,000",
    phases: [
      tx("Prep", ["Structural survey", "Building control application"]),
      tx("Construction", ["Steel & joist work", "Staircase & dormer"]),
      tx("Finishing", ["Insulation & plastering", "Decoration"]),
    ],
  },
];

/* ------------------------------------------------------------ dashboard --- */
/* Derived where it can be, so the overview never contradicts the other
   sections. See NOTES.md — the Dashboard is not in the handoff design. */

const openTickets = TICKETS.filter((t) => t.status !== "resolved").length;
const criticalTickets = TICKETS.filter((t) => t.severity === "critical" && t.status !== "resolved").length;
const activeRequests = REQUESTS.filter((r) => r.stage !== "complete" && r.stage !== "draft").length;
const lockedMilestones = REQUESTS.reduce(
  (n, r) => n + r.milestones.filter((m) => m.state === "locked").length,
  0
);
const unverified = USERS.filter((u) => Math.max(...u.personas.map((p) => p.tier)) < 2).length;
const paymentsNeedingTriage = PAYMENTS.filter((p) => p.status === "failed" || p.status === "disputed").length;

export const DASHBOARD_STATS = [
  { id: "users", kicker: "Accounts", value: String(USERS.length), label: "Total users", meta: `${unverified} below tier 2`, tone: "accent" },
  { id: "tickets", kicker: "Support", value: String(openTickets), label: "Open tickets", meta: `${criticalTickets} critical`, tone: criticalTickets ? "danger" : "ok" },
  { id: "requests", kicker: "Projects", value: String(activeRequests), label: "Active requests", meta: `${lockedMilestones} milestones locked`, tone: lockedMilestones ? "warn" : "ok" },
  { id: "payments", kicker: "Revenue", value: String(paymentsNeedingTriage), label: "Payments to triage", meta: "failed or disputed", tone: paymentsNeedingTriage ? "danger" : "ok" },
];

export const DASHBOARD_ACTIVITY = [
  { id: "ac1", tone: "danger", text: "Kaur Interiors Ltd raised a critical billing ticket", meta: "TCK-1042 · 2 hours ago" },
  { id: "ac2", tone: "warn", text: "Grady Construction Ltd payment failed — card declined", meta: "TXN-88231 · 25 Jul 2026" },
  { id: "ac3", tone: "ok", text: "Roof repair — storm damage marked complete", meta: "SR-4008 · Fiona Marsh" },
  { id: "ac4", tone: "info", text: "Ashworth Group Ltd started a Growth plan trial", meta: "TXN-88250 · 22 Jul 2026" },
  { id: "ac5", tone: "warn", text: "Second fix locked — waterproofing inspection outstanding", meta: "SR-4051 · Priya Nair" },
  { id: "ac6", tone: "ok", text: "Bethan Rees reached tier 3 on Bathroom Fitter persona", meta: "Verification · 11 Oct 2023" },
];

export const TONE_COLOR = {
  danger: "var(--status-danger)",
  warn: "var(--status-warn)",
  ok: "var(--status-ok)",
  info: "var(--status-info)",
  accent: "var(--color-accent)",
  muted: "var(--status-muted)",
};
